import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { MdLock, MdAutorenew, MdContentCopy, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { updateSecuritySettings } from '../../../services/Http/Api';
import {
    generatePassword,
    copyToClipboard,
    entropyBits,
    DEFAULT_LENGTH,
    LENGTH_OPTIONS,
} from '../../../shared/utils/passwordGenerator';

const SecuritySettings = () => {
    const { t } = useTranslation();
    const [length, setLength] = useState(DEFAULT_LENGTH);
    const [revealed, setRevealed] = useState(false);
    const [generated, setGenerated] = useState(false);

    const { register, handleSubmit, reset, setValue, getValues, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            current_password: '',
            new_password: '',
            confirm_password: '',
        },
    });

    // Generirana lozinka ide u oba polja i odmah se prikazuje: skrivena
    // lozinka koju korisnik nije upisao ne moze se provjeriti, a kopirati je
    // naslijepo je nacin da se zakljuca iz racuna.
    const onGenerate = async () => {
        const value = generatePassword(length);

        setValue('new_password', value, { shouldValidate: true });
        setValue('confirm_password', value, { shouldValidate: true });
        setRevealed(true);
        setGenerated(true);

        const copied = await copyToClipboard(value);
        toast[copied ? 'success' : 'info'](
            copied
                ? t('Settings.Security.generatedAndCopied')
                : t('Settings.Security.generatedNotCopied')
        );
    };

    const onCopy = async () => {
        const value = getValues('new_password');

        if (!value) {
            toast.info(t('Settings.Security.nothingToCopy'));
            return;
        }

        const copied = await copyToClipboard(value);
        toast[copied ? 'success' : 'error'](
            copied ? t('Settings.Security.copied') : t('Settings.Security.copyFailed')
        );
    };

    const onSubmit = async (data) => {
        try {
            await updateSecuritySettings(data);
            toast.success(t('Settings.Security.saveSuccess'));
            reset();
            setRevealed(false);
            setGenerated(false);
        } catch (error) {
            const message = error?.response?.data?.message || error.message;
            toast.error(t('Settings.Security.saveError') + ': ' + message);
        }
    };

    return (
        <Container>
            <Header>
                <Title><MdLock size={30} style={{ marginRight: '10px' }} />{t('Settings.Security.title')}</Title>
            </Header>
            <Card>
                <CardHeader>
                    <CardTitle><MdLock size={20} /> {t('Settings.Security.userSecurity')}</CardTitle>
                </CardHeader>
                <CardBody>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <FormGroup>
                            <Label>{t('Settings.Security.currentPassword')}</Label>
                            <StyledInput
                                type="password"
                                {...register('current_password', { required: t('Settings.Security.currentPasswordRequired') })}
                                placeholder={t('Settings.Security.currentPasswordPlaceholder')}
                                aria-label={t('Settings.Security.currentPassword')}
                            />
                            {errors.current_password && <ErrorText>{errors.current_password.message}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('Settings.Security.newPassword')}</Label>
                            <InputRow>
                                <StyledInput
                                    type={revealed ? 'text' : 'password'}
                                    {...register('new_password', {
                                        required: t('Settings.Security.newPasswordRequired', { defaultValue: 'New password is required' }),
                                        minLength: { value: 8, message: t('Settings.Security.passwordMinLength') },
                                    })}
                                    placeholder={t('Settings.Security.newPasswordPlaceholder')}
                                    aria-label={t('Settings.Security.newPassword')}
                                    autoComplete="new-password"
                                />
                                <IconButton
                                    type="button"
                                    onClick={() => setRevealed((v) => !v)}
                                    title={t(revealed ? 'Settings.Security.hide' : 'Settings.Security.reveal')}
                                    aria-label={t(revealed ? 'Settings.Security.hide' : 'Settings.Security.reveal')}
                                >
                                    {revealed ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
                                </IconButton>
                                <IconButton
                                    type="button"
                                    onClick={onCopy}
                                    title={t('Settings.Security.copy')}
                                    aria-label={t('Settings.Security.copy')}
                                >
                                    <MdContentCopy size={18} />
                                </IconButton>
                            </InputRow>

                            <GeneratorRow>
                                <GenerateButton type="button" onClick={onGenerate}>
                                    <MdAutorenew size={17} /> {t('Settings.Security.generate')}
                                </GenerateButton>

                                <LengthSelect
                                    value={length}
                                    onChange={(e) => setLength(Number(e.target.value))}
                                    aria-label={t('Settings.Security.length')}
                                >
                                    {LENGTH_OPTIONS.map((n) => (
                                        <option key={n} value={n}>
                                            {t('Settings.Security.lengthOption', { count: n })}
                                        </option>
                                    ))}
                                </LengthSelect>

                                <Hint>{t('Settings.Security.entropyHint', { bits: entropyBits(length) })}</Hint>
                            </GeneratorRow>

                            {generated && <GeneratedNote>{t('Settings.Security.saveItSomewhere')}</GeneratedNote>}
                            {errors.new_password && <ErrorText>{errors.new_password.message}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('Settings.Security.confirmPassword')}</Label>
                            <StyledInput
                                type="password"
                                {...register('confirm_password', {
                                    validate: value => value === document.querySelector('[name="new_password"]').value || t('Settings.Security.passwordsDoNotMatch')
                                })}
                                placeholder={t('Settings.Security.confirmPasswordPlaceholder')}
                                aria-label={t('Settings.Security.confirmPassword')}
                            />
                            {errors.confirm_password && <ErrorText>{errors.confirm_password.message}</ErrorText>}
                        </FormGroup>
                        <SaveButton type="submit" disabled={isSubmitting}>
                            {t('Settings.Security.updateUserSecurity')}
                        </SaveButton>
                    </Form>
                </CardBody>
            </Card>
        </Container>
    );
};

export default SecuritySettings;

// Styled Components
const InputRow = styled.div`
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    padding: 0 0.6rem;
    border: 1px solid #ced4da;
    border-radius: 5px;
    background: #fff;
    color: rgba(130, 22, 146, 1);
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 0.07);
    }
`;

const GeneratorRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-top: 0.6rem;
    flex-wrap: wrap;
`;

const GenerateButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.45rem 0.8rem;
    border: 1px solid rgba(130, 22, 146, 1);
    border-radius: 5px;
    background: #fff;
    color: rgba(130, 22, 146, 1);
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 1);
        color: #fff;
    }
`;

const LengthSelect = styled.select`
    padding: 0.45rem 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 5px;
    background: #fff;
    font-size: 0.88rem;
`;

const Hint = styled.span`
    font-size: 0.8rem;
    color: #6b7280;
`;

const GeneratedNote = styled.p`
    margin: 0.5rem 0 0;
    font-size: 0.82rem;
    color: #8a6d00;
    background: #fff8e1;
    border-radius: 5px;
    padding: 0.5rem 0.6rem;
`;

const Container = styled.div`
    padding: 1.25rem;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
        padding: 0.625rem;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.9375rem;
    border-bottom: 2px solid rgba(130, 22, 146, 0.8);

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0.625rem;
    }
`;

const Title = styled.h3`
    color: rgba(130, 22, 146, 1);
    font-size: 1.5rem;
    font-weight: bold;
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`;

const Card = styled.div`
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-left: 4px solid rgba(130, 22, 146, 1);
    margin-bottom: 1.25rem;
`;

const CardHeader = styled.div`
    padding: 0.9375rem 1.25rem;
    background-color: rgba(130, 22, 146, 0.1);
`;

const CardTitle = styled.h5`
    margin: 0;
    color: rgba(130, 22, 146, 1);
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 0.625rem;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const CardBody = styled.div`
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.9375rem;

    @media (max-width: 768px) {
        padding: 0.625rem;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
`;

const Label = styled.label`
    font-weight: bold;
    color: #333;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.625rem;
`;

const StyledInput = styled.input`
    padding: 0.625rem;
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 5px;
    font-size: 1rem;
    outline: none;

    &:focus {
        border-color: rgba(130, 22, 146, 1);
        box-shadow: 0 0 5px rgba(130, 22, 146, 0.5);
    }
`;

const ErrorText = styled.p`
    color: #dc3545;
    font-size: 0.875rem;
`;

const SaveButton = styled.button`
    padding: 0.625rem 1.25rem;
    background-color: rgba(130, 22, 146, 1);
    color: white;
    border: none;
    border-radius: 5px;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: rgba(130, 22, 146, 0.9);
    }
`;

